import { basePathHelper } from '../../../setup/base-path.helper';
import { ErrorNormalized } from '../../errors/models/error.model';
import { PushNotificationStatus } from '../models/push-notification-status.model';

// Note: the service worker can't be placed in the static js file with the
// rest of the javascript files.
// the service worker must be at the base most "scope" (essentially the folder structure)
// see: https://developer.mozilla.org/en-US/docs/Web/API/Service_Worker_API/Using_Service_Workers#enter_service_workers
const seviceWorkerPath = import.meta.env.DEV
    ? '/public/service-worker.js'
    : basePathHelper.appPath + '/service-worker.js';

let registration: ServiceWorkerRegistration | null = null;

const canEnable = (() => {
    if (!('serviceWorker' in navigator)) {
        return false;
    }

    if (!('PushManager' in window)) {
        return false;
    }

    if (!('Notification' in window)) {
        return false;
    }

    return true;
})();

type RegistrationReturn = Promise<{
    status: PushNotificationStatus,
    applicationServerKey?: string,
    error?: ErrorNormalized
}>;

const init = async (): RegistrationReturn => {
    if (!canEnable) {
        return {
            status: 'not supported',
        };
    }

    let error: ErrorNormalized | undefined = undefined;
    try {
        registration = await navigator.serviceWorker.register(seviceWorkerPath);
    } catch (catchError) {
        error = {
            message: catchError?.toString() || 'Unable to register service worker.',
        };
    }
    const permission = Notification.permission;
    const status
        = permission === 'denied'
            ? 'denied'
            : permission === 'granted'
                ? 'granted'
                : 'unknown permission';
    return {
        status,
        error,
    };
};

// Web-Push
// Public base64 to Uint
const urlBase64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

// see: https://web.dev/push-notifications-subscribing-a-user/
const askPermission = async () => {
    const promise = await new Promise(function (resolve, reject: (reason?: unknown) => void) {
        const permissionResult = Notification.requestPermission(function (result) {
            resolve(result);
        });

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (permissionResult) {
            permissionResult.then(resolve, reject);
        }
    });
    return promise as NotificationPermission;
};

const getSubscription = (applicationServerKey: string) => {
    return registration!.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(applicationServerKey),
    });
};

export const pushNotificationHelper = {
    askPermission,
    init,
    getSubscription,
};
